import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useExperienceStore } from '../core/experienceStore'
import { ExperienceRegistry } from '../core/ExperienceRegistry'
import { Sparkles, Terminal, Shield, Play, HelpCircle } from 'lucide-react'
import styles from './ExperienceControl.module.css'

export function ExperienceControl() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  
  const {
    settings,
    setMode,
    setActiveExperienceId,
    setLifecycleState,
  } = useExperienceStore()

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectMode = (mode: string) => {
    setMode(mode)
    setIsOpen(false)
  }

  const triggerReplay = (id: string) => {
    // Explicit override trigger
    setActiveExperienceId(id)
    setLifecycleState('preloading')
    setIsOpen(false)
  }

  const allExps = ExperienceRegistry.getAll()
  const isDev = import.meta.env.DEV

  return (
    <div className={styles.wrapper} ref={dropdownRef}>
      <button
        className={`${styles.triggerBtn} ${isOpen ? styles.triggerBtnActive : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Experience Engine Settings"
        aria-label="Experience Engine Settings"
      >
        <Sparkles size={16} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <span className={styles.sectionHeader}>Engine Mode</span>
          
          <button
            className={`${styles.item} ${settings.mode === 'surprise' ? styles.itemActive : ''}`}
            onClick={() => selectMode('surprise')}
          >
            <span>Surprise Me</span>
            <HelpCircle size={12} opacity={0.6} />
          </button>

          <button
            className={`${styles.item} ${settings.mode === 'off' ? styles.itemActive : ''}`}
            onClick={() => selectMode('off')}
          >
            <span>Disabled</span>
            <Shield size={12} opacity={0.6} />
          </button>

          <div className={styles.divider} />
          <span className={styles.sectionHeader}>Instant Replay</span>

          {allExps.map((exp) => (
            <button
              key={exp.metadata.id}
              className={styles.item}
              onClick={() => triggerReplay(exp.metadata.id)}
            >
              <span>{exp.metadata.name}</span>
              <Play size={10} opacity={0.6} />
            </button>
          ))}

          {isDev && (
            <>
              <div className={styles.divider} />
              <button
                className={styles.item}
                style={{ color: 'var(--color-accent)' }}
                onClick={() => {
                  navigate('/playground')
                  setIsOpen(false)
                }}
              >
                <span>Playground Console</span>
                <Terminal size={12} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
